import React, { useState, useCallback } from "react";

/**
 * Receives children from an accordion, and clones them to place the index on each
 * which will be used when opening/closing them.
 */
export function useAccordionChildren(children) {
  return React.Children.map(children, (child, index) =>
    React.cloneElement(child, {
      ...child.props,
      index: child.props.index ?? index,
    })
  );
}

/** 
 * {
    if (child.type.name !== "AccordionItem") {
      const error = new Error(
        "The direct child of an Accordion component must be an AccordionItem"
      );
      error.name = "Invalid Accordion child";
      Error.captureStackTrace?.(error, useAccordionChildren);
      throw error;
    }
*/

/**
 * Check the supplied default values to see if there are any invalid values passed.
 * This should help prevent any unwanted bugs.
 */

function checkDefaultValues(defaultOpen, children, constructor) {
  const throwError = (hasError = false) => {
    if (!hasError) return;
    const error = new Error(
      `Your supplied default value of (${defaultOpen}) does not match the index of any children. The maximum default value can be ${
        children.length - 1
      } and the count should start from 0.
      `
    );
    error.name = "Invalid default value";

    Error.captureStackTrace?.(error, constructor);

    throw error;
  };
  Array.isArray(defaultOpen)
    ? throwError(defaultOpen.some((e) => e > children.length - 1))
    : throwError(defaultOpen > children.length - 1);
}

/**
 * This hook provides all the state management for accordions.
 */
export function useAccordion(props) {
  const {
    allowMultiple: allowMultipleProp,
    allowToggle,
    backgroundStyle = "solid",
    children,
    defaultOpen,
    ...htmlProps
  } = props;

  // If the defaultOpen that is passed is an array and has more than 1 value,
  // set allowMultiple to true by default if it has not been set already
  const allowMultiple =
    allowMultipleProp ?? (Array.isArray(defaultOpen) && defaultOpen.length > 0);

  checkDefaultValues(defaultOpen, children, useAccordion);

  const [activeItems, setActiveItems] = useState(
    allowMultiple ? defaultOpen ?? [] : defaultOpen ?? null
  );

  const handleItemClick = useCallback(
    (index) => {
      if (activeItems === null || activeItems === []) {
        activeItems === null ? setActiveItems(index) : setActiveItems([index]);
        return;
      }
      // activeItems will be an array
      if (allowMultiple) {
        if (allowToggle) {
          // If a user can open/close accordion items, check if it is included
          // in the active items. If not, add it. If it is, remove it.
          activeItems.includes(index)
            ? setActiveItems(activeItems.filter((i) => i !== index))
            : setActiveItems((prevActive) => [...prevActive, index]);
        } else {
          if (activeItems.includes(index)) return;
          setActiveItems((prevActive) => [...prevActive, index]);
        }
        // If a user can not open/close accordion items, and it does not allow
        // multiple to be open, set it to null. Otherwise, set to the index of
        // the item.
      } else if (!allowMultiple) {
        allowToggle
          ? setActiveItems((prevActive) =>
              prevActive === index ? null : index
            )
          : setActiveItems(index);
      }
    },
    [activeItems, allowMultiple, allowToggle]
  );

  // these props will be passed to the accordion context provider
  return {
    activeItems,
    allowMultiple: !!allowMultiple,
    allowToggle: !!allowToggle,
    backgroundStyle,
    defaultOpen: defaultOpen ?? null,
    handleItemClick,
    htmlProps,
  };
}
